import {
  flexRender,
  type Row,
  type Table as TanstackTable,
} from "@tanstack/react-table";
import * as React from "react";

import { DataTablePagination } from "@/components/data-table/data-table-pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getColumnPinningStyle } from "@/lib/data-table";
import { cn } from "@/lib/utils";

interface DataTableProps<TData> extends React.ComponentProps<"div"> {
  table: TanstackTable<TData>;
  actionBar?: React.ReactNode;
  ExpandedRow?: React.ComponentType<{ row: Row<TData> }>;
  getRowClassName?: (row: Row<TData>) => string | undefined;
  onRowClick?: (row: Row<TData>) => void;
}

function DataTableRow<TData>({
  row,
  ExpandedRow,
  getRowClassName,
  onRowClick,
}: {
  row: Row<TData>;
  ExpandedRow?: React.ComponentType<{ row: Row<TData> }>;
  getRowClassName?: (row: Row<TData>) => string | undefined;
  onRowClick?: (row: Row<TData>) => void;
}) {
  const canExpand = ExpandedRow && row.getCanExpand();
  const isClickable = canExpand || Boolean(onRowClick);

  return (
    <>
      <TableRow
        data-state={row.getIsSelected() && "selected"}
        className={cn(isClickable && "cursor-pointer", getRowClassName?.(row))}
        onClick={
          isClickable
            ? (e) => {
                // Don't toggle/open if clicking on interactive elements.
                const target = e.target as HTMLElement;
                if (
                  target.closest(
                    "button, a, input, textarea, select, [role='checkbox'], [role='combobox'], [data-no-row-toggle], [data-no-row-click]",
                  )
                ) return;
                if (canExpand) {
                  row.toggleExpanded();
                  return;
                }
                onRowClick?.(row);
              }
            : undefined
        }
      >
        {row.getVisibleCells().map((cell) => (
          <TableCell
            key={cell.id}
            style={{
              ...getColumnPinningStyle({ column: cell.column }),
            }}
          >
            {flexRender(
              cell.column.columnDef.cell,
              cell.getContext(),
            )}
          </TableCell>
        ))}
      </TableRow>
      {ExpandedRow && row.getIsExpanded() && (
        <TableRow className="bg-muted/20">
          <TableCell
            colSpan={row.getVisibleCells().length}
            className="py-3 whitespace-normal"
          >
            <ExpandedRow row={row} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export function DataTable<TData>({
  table,
  actionBar,
  ExpandedRow,
  getRowClassName,
  onRowClick,
  children,
  className,
  ...props
}: DataTableProps<TData>) {
  return (
    <div
      className={cn("flex w-full flex-col gap-2.5 overflow-auto", className)}
      {...props}
    >
      {children}
      <div className="overflow-hidden rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    colSpan={header.colSpan}
                    style={{
                      ...getColumnPinningStyle({ column: header.column }),
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <DataTableRow
                  key={row.id}
                  row={row}
                  ExpandedRow={ExpandedRow}
                  getRowClassName={getRowClassName}
                  onRowClick={onRowClick}
                />
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={table.getAllColumns().length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex flex-col gap-2.5">
        <DataTablePagination table={table} />
        {actionBar &&
          table.getFilteredSelectedRowModel().rows.length > 0 &&
          actionBar}
      </div>
    </div>
  );
}
